import React from 'react';
import { Form, Col, Row, Container } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Button, SvgIcon } from '@mui/material';
import { SmokingRooms as SmokingRoomsIcon } from '@mui/icons-material';
import { object, string, array } from '../../Helpers/Validators/customValidators';
import { SelectInput, MultiSelectInput } from '../../Helpers/Inputs';
import getMonth from '../../Helpers/Lists/Months';

import './TobaccoUse.scss';

export default function TobaccoUse(props) {
  const formMethods = useForm({
    resolver: yupResolver(TobaccoUse.ValidationSchema(props.ageSpecs)),
    mode: 'all',
    reValidateMode: 'all',
    defaultValues: props.formState
  });

  const { handleSubmit, trigger } = formMethods;

  // manually call setValue for fields that don't play nice with react-hook-form register
  const handleValueChange = (field, value) => {
    props.onFieldChange(field, value);
    trigger(field);
  };

  const getTobaccoTypeSelectOptions = () => {
    return [
      { value: 'Cigarettes/E-Cigarettes', label: 'Cigarettes/E-Cigarettes' },
      { value: 'Cigars', label: 'Cigars' },
      { value: 'Pipe', label: 'Pipe' },
      { value: 'Chew/Dip', label: 'Chew/Dip' },
      { value: 'Snuff', label: 'Snuff' },
      { value: 'Nicotine Patch/Gum/Pill', label: 'Nicotine Patch/Gum/Pill' },
      { value: 'Other', label: 'Other' },
    ];
  };

  const getOftenUsedSelectOptions = () => {
    return [
      { value: 'Daily', label: 'Daily' },
      { value: 'Weekly', label: 'Weekly' },
      { value: 'Monthly', label: 'Monthly' },
      { value: 'Celebratory', label: 'Celebratory' },
    ];
  };

  const getYearsSelectOptions = () => {
    const years = [];
    let yearStartBoundary;
    let yearEndBoundary;
    const basicInfoTobacco = props.basicInfo.tobacco;
    const birthdateYear = new Date(props.basicInfo.birthdate).getFullYear();
    const currentYear = new Date().getFullYear();

    switch (basicInfoTobacco) {
      case "Within the last year":
        yearStartBoundary = (currentYear - 1)
        yearEndBoundary = currentYear
        break;
      case "Within the last 1 to 3 years":
        yearStartBoundary = (currentYear - 3)
        yearEndBoundary = (currentYear - 1)
        break;
      case "Within the last 3 to 5 years":
        yearStartBoundary = (currentYear - 5)
        yearEndBoundary = (currentYear - 3)
        break;
      default:
        yearStartBoundary = birthdateYear;
        yearEndBoundary = currentYear;
        break;
    }

    for (let index = yearStartBoundary; index <= yearEndBoundary; index++) {
      years.push({ "value": index, "label": index })
    }

    return years;
  };

  const onSubmit = () => {
    props.onNext();
  };

  return (
    <Container className="tobaccoUseContainer personalize-step">
      <FormProvider {...formMethods}>
        <Form onSubmit={handleSubmit(onSubmit)} className="tobaccoForm">
          <div className="tobacco-container d-flex justify-content-center">
            <Col lg={6} md={10}>
              <div className="formFieldsContainer mx-auto">
                <div className="text-center mb-2">
                  <SvgIcon component={SmokingRoomsIcon} className="icons" fontSize="large" color="primary" />
                </div>

                <p className="title mx-auto">Tobacco Use</p>
                <p className="subTitle">I last used tobacco/ nicotine on</p>

                <Row>
                  <Col className="pe-1">
                    <div className='mb-3'>
                      <SelectInput
                        name="month"
                        id="month"
                        value={props.formState.month}
                        onChange={(e) => handleValueChange('month', e.target.value)}
                        label="Month"
                        selectOptions={getMonth()}
                      />
                    </div>
                  </Col>
                  <Col className="ps-1">
                    <div className='mb-3'>
                      <SelectInput
                        name="year"
                        id="year"
                        value={props.formState.year}
                        onChange={(e) => handleValueChange('year', e.target.value)}
                        label="Year"
                        selectOptions={getYearsSelectOptions()}
                      />
                    </div>
                  </Col>
                </Row>

                <div className='mb-3'>
                  <MultiSelectInput
                    name="typeOfTobacco"
                    id="typeOfTobacco"
                    value={props.formState.typeOfTobacco ? props.formState.typeOfTobacco : []}
                    onChange={(e) => handleValueChange('typeOfTobacco', e.target.value)}
                    label="What type of tobacco or nicotine?"
                    selectOptions={getTobaccoTypeSelectOptions()}
                  />
                </div>

                <div className='mb-3'>
                  <SelectInput
                    name="oftenUse"
                    id="oftenUse"
                    value={props.formState.oftenUse}
                    onChange={(e) => handleValueChange('oftenUse', e.target.value)}
                    label="How often do/did you use?"
                    selectOptions={getOftenUsedSelectOptions()}
                  />
                </div>
              </div>
            </Col>
          </div>
          <Row className="mt-4">
            <Col className="text-start pb-3 btn-back">
              <Button variant="outlined" size="medium" color="primary" onClick={props.onBack}>
                BACK
              </Button>
            </Col>
            <Col className="text-end pb-3">
              <Button variant="contained" size="medium" color="primary" type="submit">
                NEXT
              </Button>
            </Col>
          </Row>
        </Form>
      </FormProvider>
    </Container>
  );
}

TobaccoUse.propTypes = {
  formState: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onFieldChange: PropTypes.func.isRequired,
};

TobaccoUse.ValidationSchema = () => {
  return object().shape({
    typeOfTobacco: array()
      .min(1, 'Type Of Tobacco is required.')
      .required('Type Of Tobacco is required.'),
    oftenUse: string().required('Often Use is required.'),
    month: string().required('Month is required.'),
    year: string().required('Year is required.'),
  });
};

TobaccoUse.DefaultFormState = (prefill = false) => {
  return {
    typeOfTobacco: prefill ? ['Cigarettes'] : [],
    oftenUse: prefill ? 'Daily' : '',
    month: prefill ? 'january' : '',
    year: prefill ? '2020' : '',
  };
};
